import { ItemModel } from "lib/services/catalogical-models";
import { CatalogicalService } from "lib/services/catalogical-service";
import { useEffect, useState } from "react";

export interface MetadataOption {
  label: string;
  value: string;
}

interface UseCaseMetadata {
  categories: MetadataOption[];
  recommendedAudience: MetadataOption[];
  /** Alias for recommendedAudience — used by the contribute form */
  audiences: MetadataOption[];
  difficultyLevels: MetadataOption[];
  keyImprovementsOptions: MetadataOption[];
  /** Maps enum value → display label for categories, audience, difficulty */
  labelMap: Record<string, string>;
  loading: boolean;
}

interface CachedMetadata {
  categories: MetadataOption[];
  recommendedAudience: MetadataOption[];
  difficultyLevels: MetadataOption[];
  keyImprovementsOptions: MetadataOption[];
  labelMap: Record<string, string>;
}

const ALL_AUDIENCE = "ALL";

/** Module-level cache keyed by domain */
const metadataCache: Record<string, { data: CachedMetadata; cachedAt: number }> = {};
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const DOMAIN = "userusecase";

/** @internal Reset cache — for testing only */
export function _resetMetadataCache(): void {
  delete metadataCache[DOMAIN];
}

export function useUseCaseMetadata(): UseCaseMetadata {
  const empty: CachedMetadata = {
    categories: [],
    recommendedAudience: [],
    difficultyLevels: [],
    keyImprovementsOptions: [],
    labelMap: {},
  };
  const cached = metadataCache[DOMAIN];
  const isCacheValid = cached && Date.now() - cached.cachedAt < CACHE_TTL_MS;
  const [data, setData] = useState<CachedMetadata>(isCacheValid ? cached.data : empty);
  const [loading, setLoading] = useState(!isCacheValid);

  useEffect(() => {
    const entry = metadataCache[DOMAIN];
    if (entry && Date.now() - entry.cachedAt < CACHE_TTL_MS) {
      setData(entry.data);
      setLoading(false);
      return;
    }

    const service = new CatalogicalService<ItemModel>("userusecase");
    service
      .fetch("/domains/userusecase/metadata")
      .then((res) => res.json())
      .then((metadata) => {
        const toOptions = (arr: any[] | undefined): MetadataOption[] =>
          (arr || []).map((o: MetadataOption) => ({ label: o.label, value: o.value }));

        const cats = toOptions(metadata.enums?.categories).sort((a, b) => a.label.localeCompare(b.label));
        const audiences = toOptions(metadata.enums?.recommendedAudience)
          .filter((a) => a.value !== ALL_AUDIENCE)
          .sort((a, b) => a.label.localeCompare(b.label));
        const levels = toOptions(metadata.enums?.difficultyLevel);
        const keyImprovements = toOptions(metadata.enums?.keyImprovementsOptions);

        // Build value→label map from all enums
        const labelMap: Record<string, string> = {};
        Object.values(metadata.enums || {}).forEach((enumArr: any) => {
          if (Array.isArray(enumArr)) {
            enumArr.forEach((o: MetadataOption) => {
              if (o.value && o.label) labelMap[o.value] = o.label;
            });
          }
        });

        const result: CachedMetadata = {
          categories: cats,
          recommendedAudience: audiences,
          difficultyLevels: levels,
          keyImprovementsOptions: keyImprovements,
          labelMap,
        };
        metadataCache[DOMAIN] = { data: result, cachedAt: Date.now() };
        setData(result);
      })
      .catch(() => {
        setData(empty);
      })
      .finally(() => setLoading(false));
  }, []);

  return { ...data, audiences: data.recommendedAudience, loading };
}
